// SPDX-License-Identifier: AGPL-3.0-or-later
import './styles.css';
import { initTooltip } from './components/tooltip';
import { initGlossary, gloss, hideGlossary } from './glossary';
import { esc, num, delta, people } from './format';
import type { Dataset } from './types';
import { renderRegionDrawer, renderInstitutionDrawer } from './drilldown';
import { renderOverview } from './views/overview';
import { renderMap } from './views/map';
import { renderDeserts } from './views/deserts';
import { renderBanks } from './views/banks';
import { renderRemoteness } from './views/remoteness';
import { renderRankings } from './views/rankings';
import { renderTimeline } from './views/timeline';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';

const VIEWS = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'deserts', label: 'Banking deserts', render: renderDeserts },
  { id: 'banks', label: "Who's closing", render: renderBanks },
  { id: 'remoteness', label: 'Remoteness', render: renderRemoteness },
  { id: 'rankings', label: 'Rankings', render: renderRankings },
  { id: 'timeline', label: 'Timeline', render: renderTimeline },
  { id: 'explorer', label: 'Explorer', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
] as const;

export type ViewId = (typeof VIEWS)[number]['id'];
const STORE_KEY = 'au-branches:view';

interface Route { view: ViewId; region?: string; bank?: string; filter?: string }

function parseHash(): Route {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const view = (params.get('v') ?? '') as ViewId;
  const known = VIEWS.some((v) => v.id === view);
  const stored = localStorage.getItem(STORE_KEY) as ViewId | null;
  return {
    view: known ? view : (VIEWS.some((v) => v.id === stored) && stored ? stored : 'overview'),
    region: params.get('r') ?? undefined,
    bank: params.get('b') ?? undefined,
    filter: params.get('q') ?? undefined,
  };
}

export function navigate(patch: Partial<Route>, replace = false): void {
  const cur = parseHash();
  const next = { ...cur, ...patch };
  const params = new URLSearchParams();
  params.set('v', next.view);
  if (next.region) params.set('r', next.region);
  if (next.bank) params.set('b', next.bank);
  if (next.filter) params.set('q', next.filter);
  const url = `#${params.toString()}`;
  if (replace) { history.replaceState(null, '', url); render(); }
  else location.hash = url;
}

let data: Dataset | null = null;

async function load(): Promise<Dataset> {
  const bust = `?v=${Date.now().toString(36).slice(0, 6)}`;
  const [national, regions, institutions, deserts, points, meta] = await Promise.all(
    ['national', 'regions', 'institutions', 'deserts', 'points', 'meta'].map(async (f) => {
      const res = await fetch(`data/${f}.json${bust}`);
      if (!res.ok) throw new Error(`Could not load ${f}.json (HTTP ${res.status})`);
      return res.json();
    }),
  );
  return { national, regions, institutions, deserts, points, meta } as Dataset;
}

function shell(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <div class="brand">
          <img class="logo" src="/favicon.svg" alt="" width="22" height="22" />
          <h1>Bank Branches</h1>
          <span class="brand-sub">Australia's disappearing physical banking</span>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" id="about-btn" aria-label="About this site" title="About this site">?</button>
      </div>
      <div class="nav-wrap">
        <nav class="nav-tabs" role="tablist" aria-label="Views">
          ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
        </nav>
      </div>
    </header>
    <main class="main-content" id="view-root"><div class="skeleton"></div></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <p id="footer-source"></p>
        <p>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></p>
      </div>
    </footer>
    <div class="overlay" id="overlay"></div>
    <aside class="drawer" id="drawer" role="dialog" aria-label="Detail" aria-modal="true"></aside>
    <div class="modal" id="about-modal" role="dialog" aria-label="About this site" aria-modal="true"></div>
  `;

  app.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      localStorage.setItem(STORE_KEY, btn.dataset.view!);
      navigate({ view: btn.dataset.view as ViewId, region: undefined, bank: undefined, filter: undefined });
    });
  });

  const header = document.querySelector<HTMLElement>('.site-header')!;
  const syncHeaderHeight = () => document.documentElement.style.setProperty('--sticky-top', `${header.offsetHeight}px`);
  syncHeaderHeight();
  new ResizeObserver(syncHeaderHeight).observe(header);

  const drawerEl = document.getElementById('drawer')!;
  drawerEl.addEventListener('transitionend', (e) => {
    if (e.propertyName === 'transform' && !drawerEl.classList.contains('open')) drawerEl.style.display = 'none';
  });

  document.getElementById('about-btn')!.addEventListener('click', openAbout);
  document.getElementById('overlay')!.addEventListener('click', closeOverlays);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeOverlays(); });
}

function closeOverlays(): void {
  document.getElementById('overlay')!.classList.remove('open');
  document.getElementById('drawer')!.classList.remove('open');
  document.getElementById('about-modal')!.classList.remove('open');
  hideGlossary();
  const cur = parseHash();
  if (cur.region || cur.bank) navigate({ region: undefined, bank: undefined }, true);
}

function openAbout(): void {
  const m = document.getElementById('about-modal')!;
  const meta = data?.meta;
  const c = meta?.counts;
  m.innerHTML = `
    <button class="icon-btn modal-close" aria-label="Close">✕</button>
    <h2>About this site</h2>
    <p>Every physical place you can do your banking in Australia — a ${gloss('branch')}, a ${gloss('bankpost', 'Bank@Post')}, an ${gloss('other', 'other face-to-face point')} or a bank-owned ${gloss('atm', 'ATM')} — counted once a year from ${esc(meta?.firstYear ?? '')} to ${esc(meta?.latestYear ?? '')} and put on a map. It exists to answer one question banks won't: where is physical banking disappearing, and who is being left without it?</p>

    <h3>Where the data comes from</h3>
    <p>APRA, the banking regulator, requires every ${gloss('adi', 'authorised deposit-taking institution')} to report its physical service points each June, with a location precise enough to place on a map and an ABS remoteness code. This site reads that workbook, joins it to ABS resident population so regions can be compared fairly, and to official ABS boundaries so it can be mapped.</p>
    <ul>
      <li>APRA ADI Points of Presence — ${esc(String(c?.years ?? 0))} annual snapshots, ${esc(num(c?.points))} service points in the latest year</li>
      <li>ABS Estimated Resident Population (${esc(String(meta?.erpYear ?? ''))}) — the per-capita denominator</li>
      <li>ABS ASGS 2021 SA3 boundaries</li>
    </ul>

    <h3>The headline</h3>
    <p>Australia had <strong>${num(c?.branches2017)}</strong> bank branches in ${esc(meta?.firstYear)}. By ${esc(meta?.latestYear)} it had <strong>${num(c?.branchesNow)}</strong> — a fall of <strong>${delta(c?.branchChangePct)}</strong>. Bank-owned ATMs fell even faster (${delta(c?.atmChangePct)}). But ${gloss('bankpost', 'Bank@Post')} barely moved (${delta(c?.bankpostChangePct)}): as branches closed, Australia Post quietly became the last counter standing in hundreds of towns.</p>
    <p><strong>${num(c?.sa2LostLastCount)}</strong> towns have lost their last branch since ${esc(meta?.firstYear)} — home to about <strong>${people(c?.popAffected)}</strong> people. ${num(c?.sa2LostLastWithBankpost)} of them still have a Bank@Post; <strong>${num(c?.sa2LostLastNothing)}</strong> have no staffed banking of any kind left.</p>

    <h3>How to read the numbers</h3>
    <ul>
      <li><strong>EFTPOS is left out.</strong> APRA also counts the ~700,000 card machines in shops. ${gloss('eftpos', 'Those are a way to pay a merchant')}, not to do your banking, so they are excluded from every figure here.</li>
      <li><strong>Rates use resident population.</strong> ${gloss('perCapita', 'Branches per 10,000 residents')} makes a country town comparable to a city suburb.</li>
      <li><strong>“Lost its last branch” is town-level.</strong> It uses ${gloss('sa2', 'ABS SA2 areas')} — a whole ${gloss('sa3', 'SA3')} rarely hits zero, but individual towns within it do.</li>
      <li><strong>Bank-owned ATMs only.</strong> The independent machines in pubs and servos are not in this collection.</li>
    </ul>

    <h3>Updates</h3>
    <p>APRA publishes once a year, each October; this site refreshes on the same annual cadence. Data last rebuilt ${esc(meta ? new Date(meta.generated).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '')}.</p>
  `;
  m.querySelector('.modal-close')!.addEventListener('click', closeOverlays);
  document.getElementById('overlay')!.classList.add('open');
  m.classList.add('open');
  (m.querySelector('.modal-close') as HTMLElement).focus();
}

export function openRegion(code: string): void { navigate({ region: code, bank: undefined }); }
export function openInstitution(abn: string): void { navigate({ bank: abn, region: undefined }); }

function render(): void {
  if (!data) return;
  const route = parseHash();
  document.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((b) => {
    b.setAttribute('aria-selected', String(b.dataset.view === route.view));
  });

  const root = document.getElementById('view-root')!;
  const view = VIEWS.find((v) => v.id === route.view) ?? VIEWS[0];
  root.scrollTop = 0;
  try {
    view.render(root, data, route.filter);
  } catch (err) {
    root.innerHTML = `<div class="error-state"><p>Something went wrong drawing this view.</p><p>${esc(err instanceof Error ? err.message : String(err))}</p></div>`;
  }

  const drawer = document.getElementById('drawer')!;
  const overlay = document.getElementById('overlay')!;
  const aboutOpen = document.getElementById('about-modal')!.classList.contains('open');
  let opened = false;
  if (route.region) {
    const region = data.regions.find((r) => r.code === route.region);
    if (region) { renderRegionDrawer(drawer, region, data); opened = true; }
  } else if (route.bank) {
    const inst = data.institutions.find((i) => i.abn === route.bank);
    if (inst) { renderInstitutionDrawer(drawer, inst, data); opened = true; }
  }
  if (opened) {
    drawer.querySelector('.drawer-close')?.addEventListener('click', closeOverlays);
    overlay.classList.add('open');
    drawer.style.display = 'block';
    void drawer.offsetWidth;
    drawer.classList.add('open');
    (drawer.querySelector('.drawer-close') as HTMLElement | null)?.focus();
  } else if (!aboutOpen) {
    drawer.classList.remove('open');
    overlay.classList.remove('open');
  }
}

async function boot(): Promise<void> {
  shell();
  initTooltip();
  initGlossary();
  window.addEventListener('hashchange', render);

  try {
    data = await load();
  } catch (err) {
    document.getElementById('view-root')!.innerHTML = `
      <div class="error-state">
        <p>Could not load the bank branches data.</p>
        <p>${esc(err instanceof Error ? err.message : String(err))}</p>
        <p><button class="icon-btn" style="width:auto;padding:0 12px;border-radius:8px" onclick="location.reload()">Retry</button></p>
      </div>`;
    return;
  }

  const c = data.meta.counts;
  document.getElementById('footer-source')!.innerHTML =
    `Source: APRA Authorised deposit-taking institutions' points of presence (${esc(data.meta.sourcePeriodLabel)}), ` +
    `ABS Estimated Resident Population ${esc(String(data.meta.erpYear))} and ABS ASGS 2021 boundaries. ` +
    `${esc(num(c.branchesNow))} branches, ${esc(num(c.bankpostNow))} Bank@Post and ${esc(num(c.atmNow))} bank ATMs in ${esc(String(data.meta.latestYear))}. Rates are per 10,000 residents.`;

  render();
}

void boot();
